import React, { ReactElement, useMemo } from 'react';
import { Typography, Button, TooltipProps } from '@mui/material';
import { trackEvent } from '@surfline/web-common';

import Information from 'components/Information';
import PadlockIcon from 'components/Icons/Padlock';
import config from 'config';

import styles from './InformationPremiumPlus.module.scss';

const UPGRADE_URL = `${config.surflineHost}/upgrade`;

interface SegmentProperties {
  location: string; // used as uiElement in dialog
  spotId?: string;
  spotName?: string;
  subregionId?: string;
}

const DialogSubHeader: React.FC = () => (
  <Typography className={styles.dialogSubHeader} component="h4" variant="callout1" mb={2}>
    <PadlockIcon /> Premium+ Feature
  </Typography>
);

const DialogFooter: React.FC<{ segmentProps: SegmentProperties }> = ({ segmentProps }) => {
  const onClickHandler = () => {
    trackEvent('Clicked Subscribe CTA', segmentProps);
  };

  return (
    <div className={styles.dialogFooter}>
      <Button
        aria-label="premium upgrade link"
        data-testid="premium-plus-upgrade-link"
        disableFocusRipple
        disableRipple
        disableTouchRipple
        href={UPGRADE_URL}
        onClick={onClickHandler}
        variant="primary"
      >
        Get Premium+
      </Button>
    </div>
  );
};

interface InformationPremiumPlusProps {
  articleId: string;
  children?: ReactElement;
  className?: string;
  segmentProps: SegmentProperties;
  tooltipText: string;
  tooltipPlacement?: TooltipProps['placement'];
}

const InformationPremiumPlus: React.FC<InformationPremiumPlusProps> = ({
  articleId,
  children,
  className,
  segmentProps,
  tooltipText,
  tooltipPlacement = 'top-start',
}) => {
  const segmentPropsFooterCTA = useMemo(
    () => ({
      location: segmentProps.location,
      spotId: segmentProps.spotId,
      spotName: segmentProps.spotName,
      subregionId: segmentProps.subregionId,
    }),
    [segmentProps],
  );

  const segmentPropsInformation = useMemo(
    () => ({
      modalType: 'premium-plus',
      spotId: segmentProps.spotId,
      spotName: segmentProps.spotName,
      subregionId: segmentProps.subregionId,
      uiElement: segmentProps.location,
    }),
    [segmentProps],
  );

  return (
    <Information
      DialogFooter={<DialogFooter segmentProps={segmentPropsFooterCTA} />}
      DialogSubHeader={<DialogSubHeader />}
      articleId={articleId}
      className={className}
      dialogClassName={styles.dialogWrapper}
      segmentProps={segmentPropsInformation}
      tooltip={{
        componentsProps: {
          tooltip: {
            className: styles.tooltip,
          },
        },
        placement: tooltipPlacement,
        title: tooltipText,
      }}
    >
      {children}
    </Information>
  );
};

export default InformationPremiumPlus;
